/**
 * `/attendance` — WP-K3. The screen where somebody looks at other people.
 *
 * `web/shapes/attendance.md` is the shape file, committed before this code, and it
 * carries the decisions rather than this header repeating them. What is worth
 * saying here is what the file *composes*, and the two facts that shape it:
 *
 *  - **Two reads, deliberately shaped so each page boundary is explainable.**
 *    `RosterList` is one day × many employees; `MonthCalendar` is one employee × one
 *    month. `GET /attendance` pages its grid employee-major, so any other shape puts
 *    a page boundary in the middle of somebody's month.
 *  - **Every filter is convenience, and the server decides.** `nav/menu.ts`'s boxed
 *    comment is the rule for this whole build: hiding a row is not a permission
 *    check. `department_id` and `employee_id` travel to the server and are
 *    intersected with the resolved scope; the status filter never leaves the
 *    browser and says so where it is used.
 *
 * ── One thing this screen does not do ───────────────────────────────────────
 *
 * It does not compute today. `SessionUser.timezone` is the company's zone and
 * `localDateIn` resolves "today" in it, once, here — because "today" is a different
 * day in Hanoi and in Berlin, and every clamp, default and heading below depends on
 * getting that right rather than on where the reader is sitting.
 */

import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';

import { useI18n } from '../i18n';
import { DateField } from '../components/DateField';
import { DepartmentSelect } from '../components/DepartmentSelect';
import { formatDay, formatMonth, localDateIn, timezoneName } from '../i18n/datetime';
import type { AttendanceBadgeStatus, AttendanceGridDay } from '../api/types';
import { ATTENDANCE_STATUSES } from '../api/types';
import { useSession } from '../auth/SessionProvider';
import { monthOf, monthRange, type Month } from '../attendance/month';
import { newestEventAt, STATUS_PRESENTATION } from '../attendance/status';
import { ScreenHeading } from '../shell/ScreenHeading';
import { LastSync } from '../states/LastSync';
import { CorrectionQueue } from './attendance/CorrectionQueue';
import { DayDrawer } from './attendance/DayDrawer';
import { ExportSlot } from './attendance/ExportSlot';
import { MonthCalendar } from './attendance/MonthCalendar';
import { RosterList } from './attendance/RosterList';

interface OpenDay {
  readonly employeeId: string;
  readonly date: string;
}

export interface AttendanceScreenProps {
  /**
   * The clock, as a parameter. The same seam Task 42 put on `MyActivityScreen`, and
   * the same reason `calc.ts` takes `currentSourceVersion` rather than importing it:
   * every date on this screen is derived from "today", so a test has to be able to
   * hold it still — and the interesting cases are all edges, the last day of a month
   * and the day after today.
   */
  readonly nowMs?: number;
}

export function AttendanceScreen({ nowMs }: AttendanceScreenProps = {}): ReactNode {
  const { t, locale } = useI18n();
  const { user } = useSession();
  const timezone = user?.timezone ?? 'UTC';
  const permissions = user?.permissions ?? [];

  // Resolved once, in the company's zone. Every clamp below depends on it.
  const today = useMemo(() => localDateIn(timezone, nowMs ?? Date.now()), [timezone, nowMs]);

  // ── The drill-down seed (Task 44) ────────────────────────────────────────
  //
  // Spec 01 §12 requires a drill-down to keep the filter context, and `/overview`
  // holds that context in its query string. These three parameters are the whole
  // handshake, and they only ever seed the **initial** state: once the screen is
  // open the controls own it, so nothing here fights a person who changes the date
  // after arriving. Every parameter is optional and an absent one leaves the default
  // exactly where Task 43 put it — which is why none of that task's assertions move.
  //
  // The subject's *name* is deliberately not in the URL. `GET /employees`
  // (Task T2-A) exists now, but a name in a link would still need a lookup before
  // the URL could be built; it is resolved from the roster response instead (see
  // `onRoster` below), which is already scoped to this caller and fetched anyway,
  // and until then the id stands in for it.
  // Read through the router rather than `window.location.search`: the router is the
  // component that owns what the current URL means, and a `basename` or a hash
  // history would make the two disagree. Captured **once**, so the controls below
  // own the state from the first render and nothing here fights a person who
  // changes the date after arriving.
  const [searchParams] = useSearchParams();
  const [seed] = useState(() => new URLSearchParams(searchParams));
  const seededDate = seed.get('date');
  const seededDepartment = seed.get('department_id');
  const seededEmployee = seed.get('employee_id');
  const initialDate = seededDate !== null && seededDate <= today ? seededDate : today;

  const [date, setDate] = useState(initialDate);
  const [month, setMonth] = useState<Month>(() => monthOf(initialDate));
  const [departmentId, setDepartmentId] = useState<string | null>(seededDepartment);
  const [statusFilter, setStatusFilter] = useState<AttendanceBadgeStatus | ''>('');
  const [subject, setSubject] = useState<{ id: string; name: string } | null>(
    seededEmployee === null ? null : { id: seededEmployee, name: seededEmployee },
  );
  const [roster, setRoster] = useState<readonly AttendanceGridDay[]>([]);
  const [open, setOpen] = useState<OpenDay | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  // Where focus goes back to when the drawer closes. Without it a keyboard user is
  // returned to the top of the document and re-walks the month to get back.
  const opener = useRef<HTMLElement | null>(null);

  const onRoster = useCallback((rows: readonly AttendanceGridDay[]) => {
    setRoster(rows);
    // A subject seeded from the URL arrives with its id standing in for its name.
    // `GET /employees` (Task T2-A) exists now, but this screen has not been rewired
    // to call it for this: the roster the server just answered with already carries
    // the name **and** is already narrowed to this caller's scope, so reading the
    // name off it costs nothing extra and a name can never appear here for somebody
    // outside that scope.
    setSubject((current) => {
      if (current === null || current.name !== current.id) return current;
      const match = rows.find((row) => row.employee_id === current.id);
      return match === undefined ? current : { id: current.id, name: match.employee_name };
    });
  }, []);

  const onOpenDay = useCallback((employeeId: string, openDate: string) => {
    opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setOpen({ employeeId, date: openDate });
  }, []);

  const onCloseDrawer = useCallback(() => {
    setOpen(null);
    opener.current?.focus();
  }, []);

  const onSelectEmployee = useCallback((day: AttendanceGridDay) => {
    setSubject({ id: day.employee_id, name: day.employee_name });
  }, []);

  const onDecided = useCallback(() => {
    // A settled decision writes `approved_offline_seconds` and enqueues a recalc, so
    // the grid it changed is re-read rather than left showing the pre-decision row.
    setReloadToken((value) => value + 1);
  }, []);

  const range = monthRange(month, today);
  const agentSyncAt = useMemo(() => newestEventAt(roster), [roster]);

  return (
    <div className="wp-stack wp-b">
      <div className="wp-screen-head">
        <ScreenHeading>{t('nav.attendance')}</ScreenHeading>
        {/* Both timestamps, both named. `achauSyncAt` is `null` because no session
            endpoint publishes `wp_sync_state`; it renders "chưa từng" rather than
            being hidden, since a missing last-updated line reads as an oversight. */}
        <LastSync
          agentSyncAt={agentSyncAt}
          achauSyncAt={null}
          timezone={timezone}
          testId="k3-last-sync"
        />
      </div>

      <p className="wp-small wp-muted wp-b-lede" data-testid="k3-window">
        {t('k3.window', {
          month: formatMonth(month, locale),
          date: formatDay(date, locale, { day: 'numeric', month: 'long', year: 'numeric' }),
          timezone: timezoneName(timezone, locale),
        })}
      </p>

      <fieldset className="wp-filters" data-testid="k3-filters">
        {/* Visually hidden, not deleted: printed it was a caps eyebrow directly above
            the first field's own caps label (finish review, minor b), and a
            `<fieldset>` with no legend announces as an unnamed group. */}
        <legend className="wp-visually-hidden">{t('k3.filters.legend')}</legend>

        <DateField
          id="k3-date"
          testId="k3-date"
          label={t('k3.filter.date')}
          value={date}
          max={today}
          onChange={(next) => {
            if (next === '') return;
            setDate(next);
            setMonth(monthOf(next));
          }}
        />

        {/* A picker, not a typed id: `GET /employees/departments` has been mounted
            since Task T2-A and the shared control reads it (finish review, fix 4). */}
        <DepartmentSelect
          id="k3-department"
          testId="k3-department"
          label={t('k3.filter.department')}
          value={departmentId}
          onChange={setDepartmentId}
        />

        <div className="wp-field">
          <label htmlFor="k3-employee">{t('k3.filter.employee')}</label>
          {/* Options come from the roster the server just answered with, so this
              control can never name a subject outside the caller's scope — not as a
              guard, but because there is no other source: `openapi.yaml` has no
              employee directory endpoint. */}
          <select
            id="k3-employee"
            value={subject?.id ?? ''}
            onChange={(event) => {
              const found = roster.find((row) => row.employee_id === event.target.value);
              setSubject(
                found === undefined ? null : { id: found.employee_id, name: found.employee_name },
              );
            }}
            data-testid="k3-employee"
          >
            <option value="">{t('k3.filter.all')}</option>
            {roster.map((row) => (
              <option key={row.employee_id} value={row.employee_id}>
                {row.employee_name}
              </option>
            ))}
          </select>
        </div>

        <div className="wp-field">
          <label htmlFor="k3-status">{t('k3.filter.status')}</label>
          <select
            id="k3-status"
            value={statusFilter}
            onChange={(event) => {
              setStatusFilter(event.target.value as AttendanceBadgeStatus | '');
            }}
            data-testid="k3-status"
          >
            <option value="">{t('k3.filter.all')}</option>
            {ATTENDANCE_STATUSES.map((value) => (
              <option key={value} value={value}>
                {t(STATUS_PRESENTATION[value].labelKey)}
              </option>
            ))}
          </select>
        </div>
      </fieldset>

      {/* The decisions come before the numbers. `PRODUCT.md` §2.2: HR's work on this
          screen **is** a queue, so the thing they are here to settle is the first
          block under the filters and not a panel at the bottom of the page.

          Gated for honesty, not for enforcement: the decision endpoints require
          `workforce.manage_shifts`, so offering the panel to a session without it
          teaches the reader to expect a refusal. The server refuses either way —
          `nav/menu.ts` says why that is the only correct reading of this line. */}
      {permissions.includes('workforce.manage_shifts') ? (
        <CorrectionQueue timezone={timezone} onDecided={onDecided} />
      ) : null}

      {/* The wall timesheet, at the full width of the paper. It stays out of the
          drawer's split on purpose: every cell prints its status as a **word** next
          to its glyph (DESIGN.md §4 rule 4), and in a two-thirds column the seven
          Vietnamese labels have nowhere to go but an ellipsis — which turns the
          redundant channel the rule exists for back into colour alone. */}
      <MonthCalendar
        month={month}
        today={today}
        timezone={timezone}
        employeeId={subject?.id ?? null}
        employeeName={subject?.name ?? null}
        onOpenDay={onOpenDay}
        onMonthChange={setMonth}
        reloadToken={reloadToken}
      />

      {/* The day, and the day that was opened out of it — a right-hand column on a
          desk monitor, a panel under the list on anything narrower (`group-b.css`).
          Never an overlay: `DayDrawer` keeps no focus trap, deliberately, and a
          floating panel that cannot trap focus correctly loses a keyboard user in
          the page behind it. */}
      <div className="wp-b-split" data-drawer={open === null ? 'closed' : 'open'}>
        <div className="wp-b-split__main">
          <RosterList
            date={date}
            timezone={timezone}
            departmentId={departmentId}
            statusFilter={statusFilter}
            selectedEmployeeId={subject?.id ?? null}
            onSelectEmployee={onSelectEmployee}
            onOpenDay={onOpenDay}
            onRoster={onRoster}
            reloadToken={reloadToken}
          />
        </div>

        {open === null ? null : (
          <DayDrawer
            key={`${open.employeeId}|${open.date}`}
            employeeId={open.employeeId}
            date={open.date}
            timezone={timezone}
            onClose={onCloseDrawer}
          />
        )}
      </div>

      <ExportSlot
        params={{
          date_from: range?.from ?? date,
          date_to: range?.to ?? date,
          employee_id: subject?.id ?? null,
          department_id: departmentId,
        }}
      />
    </div>
  );
}
